import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { Link } from "react-router-dom";
import { ExportSquare } from "iconsax-reactjs";
dayjs.extend(relativeTime);
dayjs.extend(advancedFormat);

const getRemark = (txn = {}) => {
    const s = String(txn.status || "").toLowerCase();
    const isFailed =
        s.includes("failed") ||
        s.includes("declined") ||
        s.includes("rejected") ||
        s.includes("error") ||
        s.includes("cancelled") ||
        s.includes("canceled");

    const failureReason =
        txn.reason ??
        txn.failureReason ??
        txn.declineReason ??
        txn.cancelReason ??
        txn.error ??
        txn.errorMessage ??
        txn.message;

    const normalRemark = txn?.narration ?? txn?.remark ?? txn?.description;

    return (normalRemark) ?? "—";
};

const normalizeStatus = (raw, paymentStatus) => {
    const s = String(raw || "").toLowerCase();
    const p = String(paymentStatus || "").toLowerCase();

    if (s === "success" || s === "completed" || s === "complete") return "Completed";
    if (p === "pending") return "Pending";
    if (s === "pending" || s === "processing") return "Processing";

    return raw || "—";
};

const buildBankIdentifiers = (txn = {}) => {
    const f = (v) => v != null && String(v).trim() !== "";

    const fields = [
        ["Account number", txn.accountNumber],
        ["Bank name", txn.bankName],
        ["Routing number", txn.routingNumber || txn.ABARouting || txn.aba],
        ["Transit number", txn.transitNumber],
        ["Institution number", txn.institutionNumber],
        ["Sort code", txn.sortCode],
        ["IBAN", txn.iban || txn.IBAN],
        ["SWIFT / BIC", txn.swiftCode || txn.swift || txn.bic],
        ["CLABE (MX)", txn.clabe],
        ["BSB (AU)", txn.bsb],
        ["IFSC (IN)", txn.ifsc],
    ];

    return fields
        .filter(([_, v]) => f(v))
        .map(([label, value]) => ({ label, value: String(value) }));
};

function pickUrl(objOrStr) {
    if (!objOrStr) return "";
    if (typeof objOrStr === "string") return objOrStr;
    if (typeof objOrStr === "object") return objOrStr.url || objOrStr.link || "";
    return "";
}

function pickName(objOrStr, fallback = "Document") {
    if (!objOrStr) return fallback;
    if (typeof objOrStr === "object" && objOrStr.name) return objOrStr.name;
    const url = pickUrl(objOrStr);
    try {
        const u = new URL(url);
        const last = decodeURIComponent(u.pathname.split("/").filter(Boolean).pop() || "");
        return last || fallback;
    } catch {
        return fallback;
    }
}

function pickSize(objOrStr) {
    if (!objOrStr || typeof objOrStr !== "object") return undefined;
    return objOrStr.size || objOrStr.fileSize || undefined;
}

function buildDocAttachments(txn = {}) {
    const candidates = [
        ["Invoice", txn.invoice ?? txn.invoiceUrl ?? txn.invoiceDocument ?? txn.Invoice],
        [
            "Other document",
            txn.supportDocument ??
                txn.supportingDocument ??
                txn.otherDocument ??
                txn.supportDoc ??
                txn.supportDocumentUrl,
        ],
    ];

    const out = [];
    for (const [label, raw] of candidates) {
        const url = pickUrl(raw);
        if (!url) continue;
        out.push({
            id: `${label.toLowerCase().replace(/\s+/g, "-")}-${url}`,
            label,
            url,
            fileName: pickName(raw, label),
            fileSize: pickSize(raw),
        });
    }
    return out;
}

function mergeAttachmentsUnique(base = [], extra = []) {
    const seen = new Set();
    const rows = [...(base || []), ...(extra || [])];
    return rows.filter((a) => {
        const key = (a.url || "") + "::" + (a.label || "");
        if (seen.has(key)) return false;
        seen.add(key);
        return Boolean(a?.url);
    });
}

export function formatTransactions(records = []) {
    return records.map((txn) => {
        const id = txn._id;
        const requestReference = txn.RequestReference;
        const route =
            txn.TransactionType === "Invoice"
                ? `/v2/payment/invoice/manage/${requestReference}`
                : `/flex/home/manage-po?orderId=${requestReference}&type=inbound`;
        const title =
            txn.TransactionType === "BalanceSwap"
                ? `Converted ${txn.currencyTo} to ${txn.currency}`
                : txn.accountName || txn.narration || "No Narration";

        const transactionType =
            txn.TransactionType === "TransactionFee"
                ? "TransactionFee"
                : txn.TransactionType === "BalanceSwap"
                ? "wallet-conversion"
                : txn.TransactionType === "FxTransfer"
                ? "bank-transfer"
                : txn.PaymentType === "credit"
                ? "wallet-funding"
                : txn.TransactionType === "OTC"
                ? "bank-transfer"
                : txn.TransactionType === "crossBorder"
                ? "bank-transfer"
                : "local-transfer";

        const time = txn.transactionDate;
        const status = normalizeStatus(txn.status, txn.paymentStatus);
        const amount = txn.PaymentType === "debit" ? -txn.amount : txn.amount;
        const currency = txn.currency;
        const attachments = txn.attachments || [];
        const paymentType = txn.PaymentType;
        const reasonForFailure = txn?.reason;

        {
            // Normalize types (include TransactionType)
            const rawType = txn.TransactionType ?? txn.transactionType ?? txn.type ?? "";
            const subType =
                txn.TransactionSubType ??
                txn.transactionSubType ??
                txn.subType ??
                txn.subtype ??
                "";
            const category = txn.TransactionCategory ?? "";

            const tNorm = String(rawType || category || subType).toLowerCase();
            const isCrypto = tNorm === "cryptotransfer";

            // currencies (used for safe fallback)
            const fromCurr = (txn.currency || txn.fromCurrency || "").toString().toUpperCase();
            const toCurr = (
                txn.currencyTo ||
                txn.recipientCurrency ||
                txn.payoutCurrency ||
                txn?.recipient?.currency ||
                ""
            )
                .toString()
                .toUpperCase();

            const looksOtcOrCross =
                tNorm === "otc" ||
                tNorm === "crossborder" ||
                tNorm === "cross-border" ||
                tNorm === "cross_border" ||
                tNorm === "fxtransfer" ||
                txn.isOTC === true ||
                txn.isCrossBorder === true ||
                (!!fromCurr && !!toCurr && fromCurr !== toCurr && !isCrypto);

            if (looksOtcOrCross) {
                const recipientGetsNum = Number(
                    txn.amountTo ?? txn.toAmount ?? txn.recipientAmount ?? 0
                );
                const recipientGets =
                    toCurr && Number.isFinite(recipientGetsNum)
                        ? `${recipientGetsNum.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                          })} ${toCurr}`
                        : "—";

                const rate =
                    txn.rate ?? txn.exchangeRate ?? txn.conversionRate ?? txn.fxRate ?? null;
                const rateStr =
                    rate != null && fromCurr && toCurr ? `1 ${fromCurr} = ${rate} ${toCurr}` : "—";

                const rawFee = txn.transactionFee ?? txn.fee ?? null;
                const feeStr =
                    rawFee != null
                        ? typeof rawFee === "number"
                            ? `${rawFee.toLocaleString()} ${fromCurr || ""}`.trim()
                            : String(rawFee)
                        : "—";

                const pair = fromCurr && toCurr ? `${fromCurr} - ${toCurr}` : "—";

                const receiverLine1 =
                    txn.receiverName || txn.accountName || txn.recipientName || txn.network || txn.networkTo || "—";
                const receiverLine2 =
                    txn.accountNumber && txn.bankName
                        ? `${txn.accountNumber} • ${txn.bankName}`
                        : txn.accountNumber || txn.bankName || "—";

                const referenceId = txn.TransactionReference ?? txn.RequestReference ?? "—";
                const remark = getRemark(txn);
            
                const details = [
                    { label: "Recipient receives", value: recipientGets },
                    { label: "Exchange rate at conversion", value: rateStr },
                    { label: "Fee", value: feeStr },
                    { label: "Currency Pair", value: pair },
                    { label: "Type", value: "Bank Transfer" },
                    { label: "Reference", value: referenceId },
                    {
                        label: "Receiver",
                        value: (
                            <div className="flex flex-col text-end">
                                <span>{receiverLine1}</span>
                                <span className="text-sm text-[#6F739B] font-normal">
                                    {receiverLine2}
                                </span>
                            </div>
                        ),
                    },
                    ...buildBankIdentifiers(txn),
                    { label: "Remark", value: remark },
                ];


                const docAttachments = buildDocAttachments(txn);
                const finalAttachments = mergeAttachmentsUnique(
                    txn.attachments || [],
                    docAttachments
                );

                return {
                    id: txn._id,
                    title:
                        txn.TransactionType === "BalanceSwap"
                            ? `Converted ${txn.currencyTo} to ${txn.currency}`
                            : txn.accountName || txn.narration || "No Narration",
                    transactionType: "bank-transfer",
                    paymentType: txn.PaymentType,
                    time: txn.transactionDate,
                    status,
                    amount: txn.PaymentType === "debit" ? -txn.amount : txn.amount,
                    currency: txn.currency,
                    details,
                    attachments: finalAttachments,
                    reasonForFailure,
                };
            }
        }

        // Bank / TradeVu / Fee
        if (
            ["BankTransfer", "FxTransfer", "TradevutoTradevu", "TransactionFee"].includes(
                txn.TransactionType
            )
        ) {
            const receiverLine1 = txn.receiverName || txn.accountName || "—";
            const receiverLine2 =
                txn.accountNumber && txn.bankName
                    ? `${txn.accountNumber} • ${txn.bankName}`
                    : txn.accountNumber || txn.bankName || "—";

            const isBankLike =
                txn.TransactionType === "BankTransfer" || txn.TransactionType === "FxTransfer";
            const isCredit = txn.PaymentType === "credit";
            const labelKey = isCredit ? "Sender" : "Receiver";

            const details = [
                {
                    label: "Type",
                    value: isBankLike
                        ? "Bank Transfer"
                        : txn.TransactionType === "TradevutoTradevu"
                        ? "TradeVu-to-TradeVu Transfer"
                        : "Fee",
                },
                { label: "Reference", value: txn.TransactionReference },
                ...(isBankLike
                    ? [
                          {
                              label: labelKey,
                              value: (
                                  <div className="flex flex-col text-end">
                                      <span>{receiverLine1}</span>
                                      <span className="text-sm text-[#6F739B] font-normal">
                                          {receiverLine2}
                                      </span>
                                  </div>
                              ),
                          },
                      ]
                    : txn.TransactionType === "TradevutoTradevu"
                    ? [
                          {
                              label: labelKey,
                              value: isCredit
                                  ? txn.senderName || txn.accountName || "—"
                                  : txn.receiverName || txn.accountName || "—",
                          },
                      ]
                    : []),
                ...buildBankIdentifiers(txn),
                { label: "Remark", value: getRemark(txn) },
            ];

            const docAttachments = buildDocAttachments(txn);
            const finalAttachments = mergeAttachmentsUnique(txn.attachments || [], docAttachments);

            return {
                id,
                title,
                transactionType,
                paymentType,
                time,
                status,
                amount,
                currency,
                details,
                attachments: finalAttachments, reasonForFailure
            };
        }

        // Crypto transfers (credit or debit)
        if (txn.TransactionType === "CryptoTransfer") {
            const sub = String(
                txn.TransactionSubType || txn.transactionSubType || txn.subType || txn.subtype || ""
            ).toUpperCase();

            const isCredit = String(txn.PaymentType || "").toLowerCase() === "credit";

            if (isCredit) {
                const typeLabel = "Credit";
                const network = txn.network ?? txn.Network ?? txn.chain ?? txn.blockchain ?? "—";
                const creditedAddress =
                    txn.accountNumber ?? txn.address ?? txn.to ?? txn.toAddress ?? "—";
                const sendersAddress =
                    txn.accountName ?? txn.senderAddress ?? txn.from ?? txn.fromAddress ?? "—";
                const txHash = txn.hash ?? txn.transactionHash ?? txn.txHash ?? "—";
                const referenceId = txn.TransactionReference ?? txn.RequestReference ?? "—";

                const details = [
                    { label: "Type", value: typeLabel },
                    { label: "Network", value: network },
                    { label: "Address", value: creditedAddress },
                    { label: "Sender’s Address", value: sendersAddress },
                    { label: "Transaction hash", value: txHash },
                    { label: "Reference ID", value: referenceId },
                ];

                const docAttachments = buildDocAttachments(txn);
                const finalAttachments = mergeAttachmentsUnique(
                    txn.attachments || [],
                    docAttachments
                );

                return {
                    id,
                    title,
                    transactionType,
                    paymentType,
                    time,
                    status,
                    amount,
                    currency,
                    details,
                    attachments: finalAttachments, reasonForFailure
                };
            }

            if (sub === "TOCRYPTO") {
                const typeLabel = "CryptoTransfer";
                const network = txn.network ?? txn.Network ?? txn.chain ?? txn.blockchain ?? "—";
                const toAddress = txn.accountNumber ?? txn.address ?? txn.to ?? "—";
                const transactionFee = txn.transactionFee ?? "—";
                const txHash = txn.hash ?? txn.transactionHash ?? txn.txHash ?? "—";
                const referenceId = txn.TransactionReference ?? txn.RequestReference ?? "—";

                const details = [
                    { label: "Type", value: typeLabel },
                    { label: "Network", value: network },
                    { label: "Fee", value: transactionFee },
                    { label: "Sent to", value: toAddress },
                    { label: "Transaction hash", value: txHash },
                    { label: "Reference ID", value: referenceId },
                ];

                const docAttachments = buildDocAttachments(txn);
                const finalAttachments = mergeAttachmentsUnique(
                    txn.attachments || [],
                    docAttachments
                );

                return {
                    id,
                    title,
                    transactionType,
                    paymentType,
                    time,
                    status,
                    amount,
                    currency,
                    details,
                    attachments: finalAttachments, reasonForFailure
                };
            }

            if (sub === "TOFIAT") {
                const fromCurr = (txn.currency || txn.fromCurrency || "USDC")
                    .toString()
                    .toUpperCase();
                const toCurr = (
                    txn.currencyTo ||
                    txn.recipientCurrency ||
                    txn.payoutCurrency ||
                    txn?.recipient?.currency ||
                    ""
                )
                    .toString()
                    .toUpperCase();

                const recipientGetsNum = Number(
                    txn.amountTo ?? txn.toAmount ?? txn.recipientAmount ?? 0
                );
                const recipientGets =
                    toCurr && isFinite(recipientGetsNum)
                        ? `${recipientGetsNum.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                          })} ${toCurr}`
                        : "—";

                const rate =
                    txn.rate ?? txn.exchangeRate ?? txn.conversionRate ?? txn.fxRate ?? null;
                const rateStr = rate != null ? `1 ${fromCurr} = ${rate} ${toCurr}` : "—";

                const rawFee = txn.transactionFee ?? txn.fee ?? null;
                const feeStr = typeof rawFee === "number" ? `${rawFee} ${fromCurr}` : rawFee || "—";

                const pair = fromCurr && toCurr ? `${fromCurr} - ${toCurr}` : "—";

                const receiverLine1 =
                    txn.receiverName || txn.accountName || txn.recipientName || "—";
                const receiverLine2 =
                    txn.accountNumber && txn.bankName
                        ? `${txn.accountNumber} • ${txn.bankName}`
                        : txn.accountNumber || txn.bankName || "—";

                const referenceId = txn.TransactionReference ?? txn.RequestReference ?? "—";
                const remark = getRemark(txn);

                const details = [
                    { label: "Recipient receives", value: recipientGets },
                    { label: "Exchange rate at conversion", value: rateStr },
                    { label: "Fee", value: feeStr },
                    { label: "Currency Pair", value: pair },
                    { label: "Type", value: "Bank transfer" },
                    {
                        label: "Receiver",
                        value: (
                            <div className="flex flex-col text-end">
                                <span>{receiverLine1}</span>
                                <span className="text-sm text-[#6F739B] font-normal">
                                    {receiverLine2}
                                </span>
                            </div>
                        ),
                    },
                    { label: "Reference ID", value: referenceId },
                    ...buildBankIdentifiers(txn),
                    { label: "Remark", value: remark },
                ];

                const docAttachments = buildDocAttachments(txn);
                const finalAttachments = mergeAttachmentsUnique(
                    txn.attachments || [],
                    docAttachments
                );

                return {
                    id,
                    title,
                    transactionType,
                    paymentType,
                    time,
                    status,
                    amount,
                    currency,
                    details,
                    attachments: finalAttachments,
                    reasonForFailure
                };
            }
        }

        // Order / Invoice / Shipment
        if (["Order", "Invoice", "Shipment"].includes(txn.TransactionType)) {
            const isOrder = txn.TransactionType === "Order";
            const firstLabel = isOrder
                ? "Purchase order"
                : txn.TransactionType === "Invoice"
                ? "Invoice"
                : "Shipment";

            const refKey = isOrder
                ? txn.orderReference
                : txn.TransactionType === "Invoice"
                ? txn.invoiceReference
                : txn.shipmentReference;

            const urlKey = isOrder
                ? txn.orderUrl
                : txn.TransactionType === "Invoice"
                ? txn.invoiceUrl
                : txn.shipmentUrl;

            const details = [
                {
                    label: firstLabel,
                    value: (
                        <Link to={route} className="!text-[#6F00FF] hover:underline">
                            {txn.TransactionType} {txn.narration} <ExportSquare size="15" />
                        </Link>
                    ),
                },
                { label: "Type", value: txn.TransactionType },
                { label: "Reference", value: txn.TransactionReference },
                { label: "Sender", value: txn.senderName || txn.accountName },
                { label: "Remark", value: getRemark(txn) },
            ];

            const docAttachments = buildDocAttachments(txn);
            const finalAttachments = mergeAttachmentsUnique(txn.attachments || [], docAttachments);

            return {
                id,
                title,
                transactionType,
                paymentType,
                time,
                status,
                amount,
                currency,
                details,
                attachments: finalAttachments, reasonForFailure
            };
        }

        // Fallback (e.g. pure BalanceSwap)
        return {
            id,
            title,
            transactionType,
            paymentType,
            time,
            status,
            amount,
            currency,
            details: [
                { label: "Amount converted", value: txn.amountTo },
                { label: "Exchange rate", value: txn.rate },
                { label: "Fee", value: txn.feeDisplay },
                { label: "Currency Pair", value: `${txn.currencyTo} – ${txn.currency}` },
                { label: "Type", value: txn.PaymentType },
                { label: "Reference", value: txn._id },
                { label: "Remark", value: getRemark(txn) },
            ],
            attachments,
            reasonForFailure
        };
    });
}

export function groupByDate(formatted = []) {
    const sections = new Map();

    const labelFor = (time) => {
        const d = dayjs(time);
        if (d.isSame(dayjs(), "day")) return "Today";
        if (d.isSame(dayjs().subtract(1, "day"), "day")) return "Yesterday";
        if (d.isAfter(dayjs().subtract(7, "day"))) return d.format("dddd");
        return d.format("ddd MMM Do, YYYY");
    };

    formatted.forEach((txn) => {
        const label = labelFor(txn.time);
        if (!sections.has(label)) sections.set(label, []);
        sections.get(label).push(txn);
    });

    return sections;
}
